import {
    Page,
    IndexTable,
    Card,
    Badge,
    Checkbox,
    ChoiceList,
    TextField,
    Autocomplete,
    Select,
    Button,
    Thumbnail,
    IndexFilters,
    Text,
    Tooltip,
    useSetIndexFiltersMode,
    IndexFiltersMode,
    useIndexResourceState,
    Icon,
} from '@shopify/polaris';
import { SafeSaveBar as SaveBar } from '../../app/components/SafeAppBridge';
import type { IndexFiltersProps, TabProps } from '@shopify/polaris';
import React, { useState, useCallback, useEffect, useMemo, useRef } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { CollectionIcon, ImageIcon, DeleteIcon } from '@shopify/polaris-icons';
import { observer } from 'mobx-react-lite';
import agent from '../../app/api/agent';
import { Product, RealtimeProduct } from '../../app/models/Product';
import type { AtdProductListItem } from '../../app/models/Atd';
import { useStore } from '../../app/stores/store';
import { getShopifyAppHandle, getShopifyStoreHandle } from '../../app/utils/shopifyAdmin';
import { truncateText } from '../../app/utils/truncateText';
import type { FitmentCatalogSettings } from '../../app/models/FitmentCatalog';
import type { SelectedCollectionMapping } from '../../app/models/CollectionMapping';
import CollectionMappingsPanel from './CollectionMappingsPanel';
import {
    getFitmentFieldEmptyOptionLabel,
    getFitmentFieldValue,
    getLegacyFieldValues,
    getVisibleFitmentFields,
    hasAnyFitmentFieldValue,
    hasRequiredFitmentFieldValues,
    isFitmentFieldDisabled,
    normalizeFitmentFieldValues,
    normalizeFitmentKey,
    updateFitmentFieldValue,
    type FitmentFieldValues,
} from '../../app/utils/fitmentFields';

type SelectOption = { label: string; value: string };
type FitmentField = ReturnType<typeof getVisibleFitmentFields>[number];

const parseYearValue = (value: string): number | null => {
    const trimmed = value.trim();
    return /^\d+$/.test(trimmed) ? Number(trimmed) : null;
};

const isStandardFitmentField = (field: FitmentField, key: string) =>
    (field.key || '').trim().toLowerCase() === key;

const orderFitmentFieldsForPlp = (fields: FitmentField[], yearField?: FitmentField) => {
    if (!yearField) {
        return fields;
    }

    const makeField = fields.find((field) => isStandardFitmentField(field, 'make'));
    const modelField = fields.find((field) => isStandardFitmentField(field, 'model'));
    const prioritizedFields = [makeField, modelField].filter((field): field is FitmentField => !!field);
    const prioritizedSet = new Set(prioritizedFields);

    return [
        ...prioritizedFields,
        ...fields.filter((field) => field !== yearField && !prioritizedSet.has(field)),
        yearField,
    ];
};

const getYearRangeValues = (startValue: string, endValue: string) => {
    const start = startValue.trim();
    const end = endValue.trim();

    if (!start && !end) {
        return { values: [''], error: '' };
    }

    if (!end || end === start) {
        return { values: [start], error: '' };
    }

    if (!start) {
        return { values: [], error: 'Choose a starting year before choosing Year To.' };
    }

    const startYear = parseYearValue(start);
    const endYear = parseYearValue(end);
    if (startYear == null || endYear == null) {
        return { values: [], error: 'Year ranges must use whole-number years.' };
    }

    if (startYear > endYear) {
        return { values: [], error: 'Year To must be the same as or after the starting year.' };
    }

    return {
        values: Array.from({ length: endYear - startYear + 1 }, (_, index) => `${startYear + index}`),
        error: '',
    };
};

const getAutocompleteOptions = (options: SelectOption[], value: string) => {
    const normalizedValue = value.trim().toLowerCase();
    const usableOptions = options.filter((option) => option.value);
    const matchingOptions = normalizedValue
        ? usableOptions.filter((option) =>
            option.label.toLowerCase().includes(normalizedValue)
            || option.value.toLowerCase().includes(normalizedValue))
        : usableOptions;

    return matchingOptions;
};

const FitmentValueAutocomplete = ({
    label,
    value,
    options,
    disabled,
    loading,
    error,
    placeholder,
    onChange,
}: {
    label: string;
    value: string;
    options: SelectOption[];
    disabled?: boolean;
    loading?: boolean;
    error?: string;
    placeholder?: string;
    onChange: (value: string) => void;
}) => {
    const containerRef = useRef<HTMLDivElement>(null);
    const filteredOptions = getAutocompleteOptions(options, value);

    useEffect(() => {
        containerRef.current
            ?.querySelectorAll<HTMLButtonElement>('.Polaris-TextField__ClearButton')
            .forEach((button) => {
                // Polaris clear buttons are focusable by default. Keeping them
                // out of tab order lets the combobox close before focus moves
                // to the next selector input.
                button.tabIndex = -1;
            });
    }, [value]);

    const textField = (
        <Autocomplete.TextField
            label={label}
            value={value}
            onChange={onChange}
            autoComplete="off"
            disabled={disabled}
            placeholder={placeholder}
            clearButton={!!value}
            onClearButtonClick={() => onChange('')}
            error={error ? true : undefined}
        />
    );

    return (
        <div ref={containerRef}>
            <Autocomplete
                options={filteredOptions}
                selected={value ? [value] : []}
                textField={textField}
                onSelect={(selected) => onChange(selected[0] ?? '')}
                loading={loading}
                emptyState={(
                    <div style={{ padding: 12, color: '#616161', fontSize: 13 }}>
                        No saved values. Type a new value to use it.
                    </div>
                )}
            />
        </div>
    );
};

const getSelectedFitmentRangeLabel = (
    fields: FitmentField[],
    values: FitmentFieldValues,
    yearField?: FitmentField,
    yearTo = '',
) =>
    fields
        .map((field) => {
            const value = getFitmentFieldValue(values, field);
            if (!value) {
                return '';
            }

            if (yearField && field === yearField && yearTo.trim() && yearTo.trim() !== value.trim()) {
                return `${value}-${yearTo.trim()}`;
            }

            return value;
        })
        .filter(Boolean)
        .join(' ');

declare global {
    interface Window {
        shopify?: {
            idToken?: () => Promise<string>;
            toast?: { show?: (msg: string | { message: string; duration?: number }) => void };
        };
    }
}

const showToast = (msg: string) => window.shopify?.toast?.show?.(msg);

const PAGE_SIZE = 50;
const PRODUCT_TITLE_DISPLAY_LIMIT = 80;

const toRealtimeProduct = (product: Product): RealtimeProduct => ({
    id: product.id,
    title: product.title,
    sku: product.sku,
    vendor: '',
    fitmentCount: product.fitmentCount,
    featuredImage: product.featuredImage,
    status: '',
    createdAt: '',
    updatedAt: '',
    isMappedToSelectedVehicle: product.isMappedToSelectedVehicle,
    isAtdProduct: product.isAtdProduct,
});

const normalizeIds = (ids: string[]) => [...ids].sort((left, right) => left.localeCompare(right));

const idsEqual = (left: string[], right: string[]) =>
    normalizeIds(left).join('|') === normalizeIds(right).join('|');

const getAtdProductTitle = (product: AtdProductListItem) =>
    [product.brandName, product.productName, product.size]
        .filter((value) => value && value.trim())
        .join(' - ') || product.partNumber || `ATD product ${product.id}`;

const getAtdStatus = (product: AtdProductListItem) => {
    if (product.isDiscontinued) return { label: 'Discontinued', tone: 'critical' as const };
    if (!product.isActive) return { label: 'Inactive', tone: 'attention' as const };
    return { label: 'Active', tone: 'success' as const };
};

const isAtdProductManuallyIncluded = (product: AtdProductListItem) =>
    product.manualSyncAction === 'Link';

const parseAtdLinkStatusFromSearch = (searchParams: URLSearchParams) => {
    const value = searchParams.get('linkStatus')?.trim().toLowerCase();
    return value === 'linked' || value === 'unlinked' ? value : 'all';
};

const parseFieldValuesFromSearch = (searchParams: URLSearchParams): FitmentFieldValues => {
    const values: FitmentFieldValues = {};
    const rawFieldValues = searchParams.get('fieldValues');

    if (rawFieldValues) {
        try {
            const parsed = JSON.parse(rawFieldValues) as Record<string, string>;
            Object.entries(parsed).forEach(([key, value]) => {
                if (typeof value === 'string' && value.trim()) {
                    values[key.trim().toLowerCase()] = value.trim();
                }
            });
        } catch {
            // Ignore malformed URL state and fall back to legacy query params.
        }
    }

    ['year', 'make', 'model', 'submodel'].forEach((key) => {
        const value = searchParams.get(key);
        if (value?.trim()) values[key] = value.trim();
    });

    return values;
};

const writeFitmentSelectionToSearchParams = (
    searchParams: URLSearchParams,
    values: FitmentFieldValues,
    yearTo: string,
) => {
    const legacyKeys = new Set(['year', 'make', 'model', 'submodel']);
    const customValues: FitmentFieldValues = {};

    // Replace the complete selector payload so navigation cannot combine a
    // current vehicle with stale values left by a previous selection.
    ['year', 'make', 'model', 'submodel', 'fieldValues', 'yearTo']
        .forEach((key) => searchParams.delete(key));

    Object.entries(values).forEach(([rawKey, rawValue]) => {
        const key = rawKey.trim().toLowerCase();
        const value = rawValue.trim();
        if (!key || !value) return;

        if (legacyKeys.has(key)) {
            searchParams.set(key, value);
        } else {
            customValues[key] = value;
        }
    });

    if (Object.keys(customValues).length > 0) {
        searchParams.set('fieldValues', JSON.stringify(customValues));
    }

    if (yearTo.trim()) {
        searchParams.set('yearTo', yearTo.trim());
    }
};

const includeSelectedOption = (options: SelectOption[], selectedValue: string): SelectOption[] => {
    if (!selectedValue || options.some((option) => option.value === selectedValue)) {
        return options;
    }

    return [
        ...options,
        { label: selectedValue, value: selectedValue },
    ];
};

type MappingRowState = 'existing' | 'pending-add' | 'pending-remove' | 'unmapped';

const getMappingRailColor = (state: MappingRowState) => {
    if (state === 'existing') return '#2f855a';
    if (state === 'pending-add') return '#2563eb';
    if (state === 'pending-remove') return '#c2410c';
    return 'transparent';
};

const ProductsRealtimePage: React.FC = () => {
    const navigate = useNavigate();
    const [searchParams, setSearchParams] = useSearchParams();
    const { userStore } = useStore();
    const [hasActiveAtdProfile, setHasActiveAtdProfile] = useState(false);
    const [atdProfileChecked, setAtdProfileChecked] = useState(false);
    const [sourceTab, setSourceTab] = useState(() => searchParams.get('tab') === 'atd' ? 1 : 0);
    const [mappingView, setMappingView] = useState<'products' | 'collections'>(() =>
        searchParams.get('view') === 'collections' ? 'collections' : 'products');
    const [collectionMappingDirty, setCollectionMappingDirty] = useState(false);
    const [fitmentCatalog, setFitmentCatalog] = useState<FitmentCatalogSettings | null>(null);
    const selectorFields = useMemo(() => getVisibleFitmentFields(fitmentCatalog), [fitmentCatalog]);
    const standardYearField = useMemo(
        () => selectorFields.find((field) => isStandardFitmentField(field, 'year')),
        [selectorFields],
    );
    const productListSelectorFields = useMemo(
        () => orderFitmentFieldsForPlp(selectorFields, standardYearField),
        [selectorFields, standardYearField],
    );
    const selectorFieldKey = useMemo(
        () => selectorFields.map((field) => normalizeFitmentKey(field)).join('|'),
        [selectorFields],
    );

    // Real-time Shopify data + cursor pagination bookkeeping.
    const [products, setProducts] = useState<RealtimeProduct[]>([]);
    const [loading, setLoading] = useState(false);
    const [hasNext, setHasNext] = useState(false);
    const [nextCursor, setNextCursor] = useState('');
    const [cursorByPage, setCursorByPage] = useState<Record<number, string>>({ 1: '' });
    const [page, setPage] = useState(1);

    // Legacy-like filters. Query goes to API; the rest are local filters to keep UI matching legacy.
    const [queryValue, setQueryValue] = useState<string>('');
    const [hasFitment, setHasFitment] = useState<string[]>([]);
    const [vendor, setVendor] = useState<string>('');
    const [taggedWith, setTaggedWith] = useState<string>('');

    const [selectedFitmentValues, setSelectedFitmentValues] = useState<FitmentFieldValues>(() => parseFieldValuesFromSearch(searchParams));
    const [selectedFitmentYearTo, setSelectedFitmentYearTo] = useState(() => searchParams.get('yearTo')?.trim() ?? '');
    const normalizedSelectedFitmentValues = useMemo(
        () => normalizeFitmentFieldValues(selectorFields, selectedFitmentValues),
        [selectorFields, selectedFitmentValues],
    );
    const selectedLegacyValues = getLegacyFieldValues(normalizedSelectedFitmentValues);
    const selectedYear = selectedLegacyValues.year;
    const selectedYearRange = useMemo(
        () => standardYearField
            ? getYearRangeValues(selectedYear, selectedFitmentYearTo)
            : { values: [''], error: '' },
        [selectedFitmentYearTo, selectedYear, standardYearField],
    );
    const selectedYearToForRequest = standardYearField && !selectedYearRange.error
        ? selectedFitmentYearTo.trim()
        : '';
    const selectedMake = selectedLegacyValues.make;
    const selectedModel = selectedLegacyValues.model;
    const [mappedOnly, setMappedOnly] = useState(() => searchParams.get('mappedOnly') === 'true');
    const [mappedCount, setMappedCount] = useState<number | null>(null);
    const [mappedCountContextKey, setMappedCountContextKey] = useState('');
    const [collectionMapping, setCollectionMapping] = useState<SelectedCollectionMapping | null>(null);
    const [collectionMappingContextKey, setCollectionMappingContextKey] = useState('');
    const latestProductRequestIdRef = useRef(0);
    const previousVehicleContextKeyRef = useRef('');

    const [fieldOptions, setFieldOptions] = useState<Record<string, SelectOption[]>>({});
    const [loadingFieldOptions, setLoadingFieldOptions] = useState(false);

    const { mode, setMode } = useSetIndexFiltersMode(IndexFiltersMode.Filtering);
    const [selectedTab, setSelectedTab] = useState(0);
    const tabs: TabProps[] = [{ id: 'all', content: 'All' }];

    const [sortSelected, setSortSelected] = useState(['created desc']);
    const searchParamString = searchParams.toString();
    const [mappingSubmitting, setMappingSubmitting] = useState(false);
    const [baselineMappedProductIds, setBaselineMappedProductIds] = useState<string[]>([]);
    const [draftMappedProductIds, setDraftMappedProductIds] = useState<string[]>([]);
    const [atdProducts, setAtdProducts] = useState<AtdProductListItem[]>([]);
    const [atdLoading, setAtdLoading] = useState(false);
    const [atdPage, setAtdPage] = useState(1);
    const [atdHasNext, setAtdHasNext] = useState(false);
    const [atdTotalCount, setAtdTotalCount] = useState(0);
    const [atdQuery, setAtdQuery] = useState('');
    const [atdStatus, setAtdStatus] = useState('all');
    const [atdLinkStatus, setAtdLinkStatus] = useState(() => parseAtdLinkStatusFromSearch(searchParams));
    const [atdSort, setAtdSort] = useState('partnumber asc');

    const currentSortField = (sortSelected[0] || 'created desc').split(' ')[0];
    const requiresLocalFitmentData = currentSortField === 'fitments' || hasFitment.length > 0 || mappedOnly;
    const hasVehicleContext = Boolean(
        productListSelectorFields.length > 0
        && !selectedYearRange.error
        && (productListSelectorFields.some((field) => field.isRequired)
            ? hasRequiredFitmentFieldValues(productListSelectorFields, normalizedSelectedFitmentValues)
            : hasAnyFitmentFieldValue(productListSelectorFields, normalizedSelectedFitmentValues))
    );

    const useLocalProductList = hasVehicleContext || requiresLocalFitmentData;
    const selectedVehicleLabel = getSelectedFitmentRangeLabel(
        productListSelectorFields,
        normalizedSelectedFitmentValues,
        standardYearField,
        selectedYearToForRequest,
    );
    const selectedVehicleContextKey = useMemo(() => {
        if (!hasVehicleContext) {
            return '';
        }

        return JSON.stringify({
            fields: Object.entries(normalizedSelectedFitmentValues)
                .sort(([leftKey], [rightKey]) => leftKey.localeCompare(rightKey)),
            yearTo: selectedYearToForRequest,
        });
    }, [hasVehicleContext, normalizedSelectedFitmentValues, selectedYearToForRequest]);
    const displayedMappedCount = mappedCountContextKey === selectedVehicleContextKey
        ? mappedCount
        : null;
    const displayedCollectionMapping = collectionMappingContextKey === selectedVehicleContextKey
        ? collectionMapping
        : null;
    const isAtdSourceTab = hasActiveAtdProfile && sourceTab === 1;
    const isCollectionsView = mappingView === 'collections';

    useEffect(() => {
        const params = new URLSearchParams(searchParamString);
        const nextMappedOnly = params.get('mappedOnly') === 'true';

        setSourceTab(params.get('tab') === 'atd' ? 1 : 0);
        setMappingView(params.get('view') === 'collections' ? 'collections' : 'products');
        setSelectedFitmentValues(parseFieldValuesFromSearch(params));
        setSelectedFitmentYearTo(params.get('yearTo')?.trim() ?? '');
        setMappedOnly(nextMappedOnly);
        setAtdLinkStatus(parseAtdLinkStatusFromSearch(params));
    }, [searchParamString]);

    useEffect(() => {
        let cancelled = false;

        const loadAtdProfileState = async () => {
            try {
                const profile = await agent.Atd.profile();
                if (!cancelled) setHasActiveAtdProfile(Boolean(profile?.isActive));
            } catch {
                if (!cancelled) setHasActiveAtdProfile(false);
            } finally {
                if (!cancelled) setAtdProfileChecked(true);
            }
        };

        loadAtdProfileState();
        return () => {
            cancelled = true;
        };
    }, []);

    useEffect(() => {
        if (atdProfileChecked && !hasActiveAtdProfile && sourceTab === 1) {
            setSourceTab(0);
        }
    }, [atdProfileChecked, hasActiveAtdProfile, sourceTab]);

    useEffect(() => {
        if (previousVehicleContextKeyRef.current === selectedVehicleContextKey) {
            return;
        }

        previousVehicleContextKeyRef.current = selectedVehicleContextKey;
        latestProductRequestIdRef.current += 1;
        setMappedCount(null);
        setMappedCountContextKey('');
        setCollectionMapping(null);
        setCollectionMappingContextKey('');
    }, [selectedVehicleContextKey]);

    const loadProducts = useCallback(async (targetPage: number, query: string, cursor: string = '', sort: string = 'created desc') => {
        if (isAtdSourceTab) {
            return;
        }

        const requestId = latestProductRequestIdRef.current + 1;
        latestProductRequestIdRef.current = requestId;
        setLoading(true);
        try {
            if (useLocalProductList) {
                const result = await agent.Products.list(
                    targetPage,
                    query,
                    hasFitment[0] || '',
                    vendor,
                    taggedWith,
                    sort,
                    selectedYear,
                    selectedYearToForRequest,
                    selectedMake,
                    selectedModel,
                    mappedOnly,
                    normalizedSelectedFitmentValues,
                );

                if (requestId !== latestProductRequestIdRef.current) {
                    return;
                }

                setProducts(result.products.map(toRealtimeProduct));
                setHasNext(result.hasNext);
                setNextCursor('');
                setMappedCount(result.mappedCount);
                setMappedCountContextKey(selectedVehicleContextKey);
                setCollectionMapping(result.collectionMapping ?? null);
                setCollectionMappingContextKey(selectedVehicleContextKey);
                setPage(targetPage);
                return;
            }

            const result = await agent.Products.listRealtime(query, cursor, PAGE_SIZE, sort);
            if (requestId !== latestProductRequestIdRef.current) {
                return;
            }

            setProducts(result.products);
            setHasNext(result.hasNext);
            setNextCursor(result.nextCursor ?? '');
            setPage(targetPage);
        } catch (error) {
            if (requestId === latestProductRequestIdRef.current) {
                throw error;
            }
        } finally {
            if (requestId === latestProductRequestIdRef.current) {
                setLoading(false);
            }
        }
    }, [hasFitment, isAtdSourceTab, mappedOnly, normalizedSelectedFitmentValues, selectedMake, selectedModel, selectedVehicleContextKey, selectedYear, selectedYearToForRequest, taggedWith, useLocalProductList, vendor]);

    useEffect(() => {
        if (isAtdSourceTab || isCollectionsView) {
            latestProductRequestIdRef.current += 1;
            setLoading(false);
            return;
        }

        // Fresh query or sort always resets paging to page 1.
        setCursorByPage({ 1: '' });
        setPage(1);
        loadProducts(1, queryValue, '', sortSelected[0] || 'created desc');
    }, [isAtdSourceTab, isCollectionsView, queryValue, sortSelected, loadProducts]);

    const loadAtdProducts = useCallback(async (
        targetPage: number,
        query: string,
        status: string,
        linkStatus: string,
        sort: string,
    ) => {
        if (!hasActiveAtdProfile) {
            setAtdProducts([]);
            setAtdTotalCount(0);
            setAtdHasNext(false);
            return;
        }

        setAtdLoading(true);
        try {
            const result = await agent.Atd.products(targetPage, query, status, linkStatus, sort);
            setAtdProducts(result.items);
            setAtdHasNext(result.hasNext);
            setAtdTotalCount(result.totalCount);
            setAtdPage(result.page);
        } finally {
            setAtdLoading(false);
        }
    }, [hasActiveAtdProfile]);

    useEffect(() => {
        if (!isAtdSourceTab) {
            return;
        }

        setAtdPage(1);
        loadAtdProducts(1, atdQuery, atdStatus, atdLinkStatus, atdSort);
    }, [atdLinkStatus, atdQuery, atdSort, atdStatus, isAtdSourceTab, loadAtdProducts]);

    useEffect(() => {
        if (!hasVehicleContext) {
            setMappedCount(null);
        }
    }, [hasVehicleContext]);

    useEffect(() => {
        let cancelled = false;

        const loadFitmentCatalog = async () => {
            try {
                const catalog = await agent.Settings.fitmentCatalog();
                if (!cancelled) setFitmentCatalog(catalog);
            } catch {
                if (!cancelled) setFitmentCatalog(null);
            }
        };

        loadFitmentCatalog();
        return () => {
            cancelled = true;
        };
    }, []);

    useEffect(() => {
        if (!fitmentCatalog) return;
        setSelectedFitmentValues((current) => normalizeFitmentFieldValues(selectorFields, current));
    }, [fitmentCatalog, selectorFieldKey, selectorFields]);

    useEffect(() => {
        if (!standardYearField) {
            setSelectedFitmentYearTo('');
        }
    }, [standardYearField]);

    useEffect(() => {
        let cancelled = false;

        const loadOptions = async () => {
            if (productListSelectorFields.length === 0) {
                setFieldOptions({});
                return;
            }

            setLoadingFieldOptions(true);
            try {
                // /products is the bulk-assignment surface, so it must use the
                // same full selector context as Vehicle page and PDP.
                const result = await agent.Vehicles.filterOptions(
                    normalizedSelectedFitmentValues.year || '',
                    normalizedSelectedFitmentValues.make || '',
                    '',
                    normalizedSelectedFitmentValues,
                    productListSelectorFields.map((field) => normalizeFitmentKey(field)),
                );

                if (cancelled) return;

                const nextOptions = productListSelectorFields.reduce<Record<string, SelectOption[]>>((accumulator, field) => {
                    const key = normalizeFitmentKey(field);
                    const values = result.optionsByField?.[key] ?? [];
                    accumulator[key] = [
                        { label: getFitmentFieldEmptyOptionLabel(field, 'all'), value: '' },
                        ...values.map((value) => ({ label: value, value })),
                    ];
                    return accumulator;
                }, {});

                setFieldOptions(nextOptions);
            } catch {
                if (!cancelled) setFieldOptions({});
            } finally {
                if (!cancelled) setLoadingFieldOptions(false);
            }
        };

        loadOptions();
        return () => {
            cancelled = true;
        };
    }, [normalizedSelectedFitmentValues, productListSelectorFields]);

    // Keep non-query filters client-side for this first realtime UI pass.
    const filteredProducts = useMemo(() => {
        if (useLocalProductList) {
            return products;
        }

        const hasTagFilter = taggedWith.trim().length > 0;

        return products.filter((product) => {
            if (vendor && !(product.vendor || '').toLowerCase().includes(vendor.toLowerCase())) return false;
            if (hasFitment[0] === 'has' && product.fitmentCount < 1) return false;
            if (hasFitment[0] === 'none' && product.fitmentCount > 0) return false;
            if (hasTagFilter) return true; // UI parity only; realtime endpoint does not return tags yet.
            if (mappedOnly && product.fitmentCount < 1) return false;
            return true;
        });
    }, [products, vendor, hasFitment, taggedWith, mappedOnly, useLocalProductList]);

    const resourceProducts = filteredProducts.map((p) => ({ ...p, id: String(p.id) }));

    const { selectedResources, allResourcesSelected, handleSelectionChange, clearSelection } =
        useIndexResourceState(resourceProducts);

    const atdResourceProducts = atdProducts.map((p) => ({ ...p, id: String(p.id) }));
    const {
        selectedResources: selectedAtdResources,
        allResourcesSelected: allAtdResourcesSelected,
        handleSelectionChange: handleAtdSelectionChange,
        clearSelection: clearAtdSelection,
    } = useIndexResourceState(atdResourceProducts);

    const mappedProductIdsOnPage = useMemo(
        () => resourceProducts
            .filter((product) => !!product.isMappedToSelectedVehicle)
            .map((product) => product.id),
        [resourceProducts],
    );
    const mappedProductIdsOnPageKey = normalizeIds(mappedProductIdsOnPage).join('|');
    const baselineMappedProductIdSet = useMemo(() => new Set(baselineMappedProductIds), [baselineMappedProductIds]);
    const draftMappedProductIdSet = useMemo(() => new Set(draftMappedProductIds), [draftMappedProductIds]);
    const visibleProductIds = useMemo(() => resourceProducts.map((product) => product.id), [resourceProducts]);
    const hasMappingDraftChanges = hasVehicleContext && !idsEqual(baselineMappedProductIds, draftMappedProductIds);
    const allVisibleProductsMapped = visibleProductIds.length > 0
        && visibleProductIds.every((id) => draftMappedProductIdSet.has(id));
    const someVisibleProductsMapped = visibleProductIds.some((id) => draftMappedProductIdSet.has(id));
    const visibleMappingCheckedState: boolean | 'indeterminate' = allVisibleProductsMapped
        ? true
        : someVisibleProductsMapped
            ? 'indeterminate'
            : false;

    useEffect(() => {
        if (!hasVehicleContext) {
            setBaselineMappedProductIds([]);
            setDraftMappedProductIds([]);
            return;
        }

        const nextMappedProductIds = mappedProductIdsOnPageKey
            ? mappedProductIdsOnPageKey.split('|')
            : [];
        setBaselineMappedProductIds(nextMappedProductIds);
        setDraftMappedProductIds(nextMappedProductIds);
    }, [hasVehicleContext, mappedProductIdsOnPageKey]);

    useEffect(() => {
        if (hasVehicleContext) {
            clearSelection();
        }
    }, [clearSelection, hasVehicleContext]);

    const getMappingRowState = useCallback((productId: string): MappingRowState => {
        const wasMapped = baselineMappedProductIdSet.has(productId);
        const isMapped = draftMappedProductIdSet.has(productId);

        if (wasMapped && isMapped) return 'existing';
        if (!wasMapped && isMapped) return 'pending-add';
        if (wasMapped && !isMapped) return 'pending-remove';
        return 'unmapped';
    }, [baselineMappedProductIdSet, draftMappedProductIdSet]);

    const selectedCount = allResourcesSelected ? resourceProducts.length : selectedResources.length;

    const onBulkFitmentAction = useCallback(async (action: 'add' | 'delete') => {
        if (selectedCount < 1) return;

        const requiresVehicleSelection = action === 'add';
        if (requiresVehicleSelection && !hasVehicleContext) {
            const requiredLabels = productListSelectorFields
                .filter((field) => field.isRequired)
                .map((field) => field.label.toLowerCase());
            showToast(requiredLabels.length > 0
                ? `Select ${requiredLabels.join(', ')} before running this action`
                : 'Select at least one selector value before running this action');
            return;
        }

        try {
            await agent.Products.bulkFitments({
                action,
                productIds: allResourcesSelected ? null : selectedResources.map((id) => Number(id)),
                scope: allResourcesSelected
                    ? {
                        query: queryValue,
                        hasFitment: hasFitment[0] || '',
                        vendor,
                        tag: taggedWith,
                    }
                    : undefined,
                year: selectedYear,
                yearTo: selectedYearToForRequest,
                make: selectedMake,
                model: selectedModel,
                submodel: selectedLegacyValues.submodel,
                fieldValues: normalizedSelectedFitmentValues,
            });

            showToast(
                action === 'add'
                    ? (hasVehicleContext ? `Mapped to ${selectedVehicleLabel}` : 'Fitments added')
                    : (hasVehicleContext ? `Unmapped from ${selectedVehicleLabel}` : 'Fitments removed')
            );

            clearSelection();
            await loadProducts(page, queryValue, cursorByPage[page] ?? '', sortSelected[0] || 'created desc');
        } catch (error: any) {
            showToast(error?.message ?? 'Failed to update fitments');
        }
    }, [
        selectedCount,
        selectedYear,
        selectedMake,
        selectedModel,
        selectedLegacyValues.submodel,
        selectedYearToForRequest,
        normalizedSelectedFitmentValues,
        hasVehicleContext,
        productListSelectorFields,
        selectedVehicleLabel,
        allResourcesSelected,
        selectedResources,
        queryValue,
        hasFitment,
        vendor,
        taggedWith,
        clearSelection,
        loadProducts,
        page,
        cursorByPage,
        sortSelected,
    ]);

    const promotedBulkActions = [
        {
            content: hasVehicleContext && selectedVehicleLabel
                ? `Map to ${selectedVehicleLabel}`
                : 'Add fitments',
            onAction: () => onBulkFitmentAction('add'),
            disabled: selectedCount < 1 || !hasVehicleContext,
        },
    ];

    const bulkActions = [
        {
            icon: DeleteIcon,
            content: hasVehicleContext && selectedVehicleLabel
                ? `Unmap from ${selectedVehicleLabel}`
                : 'Remove all fitments',
            destructive: true,
            onAction: () => onBulkFitmentAction('delete'),
            disabled: selectedCount < 1,
        },
    ];

    const toggleProductMapping = useCallback((productId: string, checked: boolean) => {
        setDraftMappedProductIds((currentIds) => {
            const nextIds = new Set(currentIds);
            if (checked) {
                nextIds.add(productId);
            } else {
                nextIds.delete(productId);
            }

            return Array.from(nextIds);
        });
    }, []);

    const toggleVisibleProductMappings = useCallback((checked: boolean) => {
        setDraftMappedProductIds((currentIds) => {
            const nextIds = new Set(currentIds);
            if (checked) {
                visibleProductIds.forEach((id) => nextIds.add(id));
            } else {
                visibleProductIds.forEach((id) => nextIds.delete(id));
            }

            return Array.from(nextIds);
        });
    }, [visibleProductIds]);

    const discardMappingChanges = useCallback(() => {
        setDraftMappedProductIds(baselineMappedProductIds);
    }, [baselineMappedProductIds]);

    const saveMappingChanges = useCallback(async () => {
        if (!hasVehicleContext || !hasMappingDraftChanges) {
            return;
        }

        const baselineIds = new Set(baselineMappedProductIds);
        const draftIds = new Set(draftMappedProductIds);
        const productIdsToMap = draftMappedProductIds.filter((id) => !baselineIds.has(id)).map((id) => Number(id));
        const productIdsToUnmap = baselineMappedProductIds.filter((id) => !draftIds.has(id)).map((id) => Number(id));

        setMappingSubmitting(true);
        try {
            if (productIdsToMap.length > 0) {
                await agent.Products.bulkFitments({
                    action: 'add',
                    productIds: productIdsToMap,
                    year: selectedYear,
                    yearTo: selectedYearToForRequest,
                    make: selectedMake,
                    model: selectedModel,
                    submodel: selectedLegacyValues.submodel,
                    fieldValues: normalizedSelectedFitmentValues,
                });
            }

            if (productIdsToUnmap.length > 0) {
                await agent.Products.bulkFitments({
                    action: 'delete',
                    productIds: productIdsToUnmap,
                    year: selectedYear,
                    yearTo: selectedYearToForRequest,
                    make: selectedMake,
                    model: selectedModel,
                    submodel: selectedLegacyValues.submodel,
                    fieldValues: normalizedSelectedFitmentValues,
                });
            }

            showToast(`Mappings saved for ${selectedVehicleLabel}`);
            await loadProducts(page, queryValue, '', sortSelected[0] || 'created desc');
        } catch (error: any) {
            showToast(error?.message ?? 'Failed to save mappings');
        } finally {
            setMappingSubmitting(false);
        }
    }, [
        baselineMappedProductIds,
        draftMappedProductIds,
        hasMappingDraftChanges,
        hasVehicleContext,
        loadProducts,
        page,
        queryValue,
        selectedMake,
        selectedModel,
        selectedLegacyValues.submodel,
        selectedVehicleLabel,
        selectedYear,
        selectedYearToForRequest,
        normalizedSelectedFitmentValues,
        sortSelected,
    ]);

    const handleHasFitmentRemove = useCallback(() => setHasFitment([]), []);
    const handleVendorRemove = useCallback(() => setVendor(''), []);
    const handleTaggedWithRemove = useCallback(() => setTaggedWith(''), []);
    const handleQueryClear = useCallback(() => setQueryValue(''), []);

    const filters = [
        {
            key: 'hasFitment',
            label: 'Has fitments',
            filter: (
                <ChoiceList
                    title="Has fitments"
                    titleHidden
                    choices={[
                        { label: 'Has fitments', value: 'has' },
                        { label: 'No fitments', value: 'none' },
                    ]}
                    selected={hasFitment}
                    onChange={setHasFitment}
                />
            ),
            shortcut: true,
        },
        {
            key: 'vendor',
            label: 'Vendor',
            filter: (
                <TextField
                    label="Vendor"
                    value={vendor}
                    onChange={setVendor}
                    autoComplete="off"
                    labelHidden
                />
            ),
            shortcut: true,
        },
        {
            key: 'taggedWith',
            label: 'Tag',
            filter: (
                <TextField
                    label="Tag"
                    value={taggedWith}
                    onChange={setTaggedWith}
                    autoComplete="off"
                    labelHidden
                />
            ),
            shortcut: true,
        },
    ];

    const appliedFilters: IndexFiltersProps['appliedFilters'] = [];
    if (hasFitment.length > 0) {
        appliedFilters.push({
            key: 'hasFitment',
            label: hasFitment[0] === 'has' ? 'Has fitments' : 'No fitments',
            onRemove: handleHasFitmentRemove,
        });
    }
    if (!isEmpty(vendor)) {
        appliedFilters.push({
            key: 'vendor',
            label: `Vendor is ${vendor}`,
            onRemove: handleVendorRemove,
        });
    }
    if (!isEmpty(taggedWith)) {
        appliedFilters.push({
            key: 'taggedWith',
            label: `Tagged with ${taggedWith}`,
            onRemove: handleTaggedWithRemove,
        });
    }

    const onHandleCancel = () => { };

    const sortOptions: IndexFiltersProps['sortOptions'] = [
        { label: 'Product title', value: 'title asc', directionLabel: 'A-Z' },
        { label: 'Product title', value: 'title desc', directionLabel: 'Z-A' },
        { label: 'Fitments', value: 'fitments asc', directionLabel: 'Lowest to highest' },
        { label: 'Fitments', value: 'fitments desc', directionLabel: 'Highest to lowest' },
        { label: 'Created', value: 'created asc', directionLabel: 'Oldest first' },
        { label: 'Created', value: 'created desc', directionLabel: 'Newest first' },
    ];

    const sortedResourceProducts = useMemo(() => {
        const [key, direction] = (sortSelected[0] || 'created desc').split(' ');
        const sign = direction === 'desc' ? -1 : 1;

        return [...resourceProducts].sort((a, b) => {
            if (key === 'fitments') return (a.fitmentCount - b.fitmentCount) * sign;
            if (key === 'created') return ((a.createdAt || '').localeCompare(b.createdAt || '')) * sign;
            if (key === 'updated') return ((a.updatedAt || '').localeCompare(b.updatedAt || '')) * sign;
            return a.title.localeCompare(b.title) * sign;
        });
    }, [resourceProducts, sortSelected]);

    const shopHandle = getShopifyStoreHandle(userStore.user?.shopDomain);
    const appHandle = getShopifyAppHandle();

    const buildProductAdminUrl = useCallback((productId: string) => {
        if (!shopHandle || !appHandle) {
            return `/products/${productId}`;
        }

        return `https://admin.shopify.com/store/${shopHandle}/apps/${appHandle}/products/${productId}`;
    }, [appHandle, shopHandle]);

    const handleTitleLinkClick = useCallback((event: React.MouseEvent<HTMLAnchorElement>, productId: string) => {
        event.stopPropagation();

        // Let the browser handle modified clicks and context-menu driven actions.
        if (
            event.button !== 0 ||
            event.metaKey ||
            event.ctrlKey ||
            event.shiftKey ||
            event.altKey
        ) {
            return;
        }

        event.preventDefault();
        navigate(`/products/${productId}`);
    }, [navigate]);

    const handleTableClickCapture = useCallback((event: React.MouseEvent<HTMLDivElement>) => {
        const clickTarget = event.target as HTMLElement;
        const clickedCheckboxColumn = clickTarget.closest('td:first-child');

        if (!clickedCheckboxColumn) {
            return;
        }

        if (hasVehicleContext) {
            const productRow = clickTarget.closest<HTMLTableRowElement>('tr[id^="pf-map-"]');
            if (!productRow) {
                return;
            }

            const productId = productRow.id.replace(/^pf-map-(existing|pending-add|pending-remove|unmapped)-/, '');
            const checkboxElement = clickedCheckboxColumn.querySelector<HTMLInputElement>('input[type="checkbox"]');

            event.stopPropagation();
            event.preventDefault();

            if (!checkboxElement || checkboxElement.disabled) {
                return;
            }

            toggleProductMapping(productId, !draftMappedProductIdSet.has(productId));
            return;
        }

        const checkboxElement = clickedCheckboxColumn.querySelector<HTMLInputElement>('input[type="checkbox"]');
        if (!checkboxElement) {
            return;
        }

        event.stopPropagation();

        if (checkboxElement.disabled) {
            event.preventDefault();
            return;
        }

        if (clickTarget !== checkboxElement) {
            event.preventDefault();
            checkboxElement.click();
        }
    }, [draftMappedProductIdSet, hasVehicleContext, toggleProductMapping]);

    const productTableHeadings: React.ComponentProps<typeof IndexTable>['headings'] = hasVehicleContext
        ? [
            {
                id: 'mapped',
                title: (
                    <div onClick={(event) => event.stopPropagation()}>
                        <Checkbox
                            label="Map all visible products"
                            labelHidden
                            checked={visibleMappingCheckedState}
                            onChange={toggleVisibleProductMappings}
                            disabled={loading || mappingSubmitting || visibleProductIds.length === 0}
                        />
                    </div>
                ),
            },
            { title: 'Product' },
            { title: 'Source' },
            { title: 'SKU' },
            { title: 'Fitments' },
            { title: '' },
        ]
        : [
            { title: 'Product' },
            { title: 'Source' },
            { title: 'SKU' },
            { title: 'Fitments' },
            { title: '' },
        ];

    const selectedAtdCount = allAtdResourcesSelected ? atdResourceProducts.length : selectedAtdResources.length;
    const selectedAtdProductIds = useMemo(
        () => allAtdResourcesSelected
            ? atdProducts.map((product) => product.id)
            : selectedAtdResources.map((id) => Number(id)).filter((id) => Number.isFinite(id)),
        [allAtdResourcesSelected, atdProducts, selectedAtdResources],
    );

    const runManualAtdLinkAction = useCallback(async (action: 'Link' | 'Unlink') => {
        if (selectedAtdProductIds.length === 0) {
            return;
        }

        setAtdLoading(true);
        try {
            const result = await agent.Atd.saveProductManualLinks({
                action,
                atdProductIds: selectedAtdProductIds,
            });
            showToast(result.message);
            clearAtdSelection();
            await loadAtdProducts(atdPage, atdQuery, atdStatus, atdLinkStatus, atdSort);
        } catch (error: any) {
            showToast(error?.message ?? `Failed to ${action.toLowerCase()} ATD products`);
        } finally {
            setAtdLoading(false);
        }
    }, [
        atdLinkStatus,
        atdPage,
        atdQuery,
        atdSort,
        atdStatus,
        clearAtdSelection,
        loadAtdProducts,
        selectedAtdProductIds,
    ]);

    const atdPromotedBulkActions = [
        {
            content: 'Link selected',
            onAction: () => runManualAtdLinkAction('Link'),
            disabled: selectedAtdCount < 1 || atdLoading,
        },
        {
            content: 'Unlink selected',
            destructive: true,
            onAction: () => runManualAtdLinkAction('Unlink'),
            disabled: selectedAtdCount < 1 || atdLoading,
        },
    ];

    const handleSourceTabSelect = useCallback((selected: number) => {
        setSourceTab(selected);

        const nextParams = new URLSearchParams(searchParams);
        writeFitmentSelectionToSearchParams(
            nextParams,
            normalizedSelectedFitmentValues,
            selectedFitmentYearTo,
        );
        if (selected === 1) {
            nextParams.set('tab', 'atd');
        } else {
            nextParams.delete('tab');
        }

        setSearchParams(nextParams, { replace: true });
    }, [normalizedSelectedFitmentValues, searchParams, selectedFitmentYearTo, setSearchParams]);

    const handleMappingViewSelect = useCallback((view: 'products' | 'collections') => {
        if (view === mappingView) return;

        if ((mappingView === 'products' && hasMappingDraftChanges)
            || (mappingView === 'collections' && collectionMappingDirty)) {
            showToast('Save or discard the current mapping changes before switching views');
            return;
        }

        setMappingView(view);
        const nextParams = new URLSearchParams(searchParams);
        writeFitmentSelectionToSearchParams(
            nextParams,
            normalizedSelectedFitmentValues,
            selectedFitmentYearTo,
        );
        if (view === 'collections') {
            nextParams.set('view', 'collections');
        } else {
            nextParams.delete('view');
        }
        setSearchParams(nextParams, { replace: true });
    }, [
        collectionMappingDirty,
        hasMappingDraftChanges,
        mappingView,
        normalizedSelectedFitmentValues,
        searchParams,
        selectedFitmentYearTo,
        setSearchParams,
    ]);

    const sourceSwitch = hasActiveAtdProfile ? (
        <div className="pf-products-source-switch" role="group" aria-label="Product source">
            <button
                type="button"
                className={`pf-products-source-switch__button${sourceTab === 0 ? ' pf-products-source-switch__button--selected' : ''}`}
                aria-pressed={sourceTab === 0}
                onClick={() => handleSourceTabSelect(0)}
            >
                Shopify
            </button>
            <button
                type="button"
                className={`pf-products-source-switch__button${sourceTab === 1 ? ' pf-products-source-switch__button--selected' : ''}`}
                aria-pressed={sourceTab === 1}
                onClick={() => handleSourceTabSelect(1)}
            >
                ATD
            </button>
        </div>
    ) : undefined;

    const mappingViewSwitch = (
        <div className="pf-products-source-switch" role="group" aria-label="Mapping type">
            <button
                type="button"
                className={`pf-products-source-switch__button${mappingView === 'products' ? ' pf-products-source-switch__button--selected' : ''}`}
                aria-pressed={mappingView === 'products'}
                onClick={() => handleMappingViewSelect('products')}
            >
                Products
            </button>
            <button
                type="button"
                className={`pf-products-source-switch__button${mappingView === 'collections' ? ' pf-products-source-switch__button--selected' : ''}`}
                aria-pressed={mappingView === 'collections'}
                onClick={() => handleMappingViewSelect('collections')}
            >
                Collections
            </button>
        </div>
    );

    const atdStatusOptions = [
        { label: 'All statuses', value: 'all' },
        { label: 'Active', value: 'active' },
        { label: 'Discontinued', value: 'discontinued' },
        { label: 'Inactive', value: 'inactive' },
        { label: 'Unavailable', value: 'unavailable' },
    ];

    const atdLinkOptions = [
        { label: 'All link states', value: 'all' },
        { label: 'Linked', value: 'linked' },
        { label: 'Unlinked', value: 'unlinked' },
    ];

    const atdSortOptions = [
        { label: 'Part number A-Z', value: 'partnumber asc' },
        { label: 'Part number Z-A', value: 'partnumber desc' },
        { label: 'Product A-Z', value: 'title asc' },
        { label: 'Product Z-A', value: 'title desc' },
        { label: 'Brand A-Z', value: 'brand asc' },
        { label: 'Brand Z-A', value: 'brand desc' },
        { label: 'Last updated newest', value: 'updated desc' },
        { label: 'Last updated oldest', value: 'updated asc' },
        { label: 'Linked first', value: 'linked desc' },
        { label: 'Unlinked first', value: 'linked asc' },
    ];

    const atdProductTableHeadings: React.ComponentProps<typeof IndexTable>['headings'] = [
        { title: 'Product' },
        { title: 'Part #' },
        { title: 'Brand' },
        { title: 'Size' },
        { title: 'Status' },
        { title: 'Shopify' },
        { title: 'Qty' },
    ];

    const fitmentSelector = (
        <div className="pf-fitment-selector">
            {productListSelectorFields.map((field) => {
                const key = normalizeFitmentKey(field);
                const selectedValue = getFitmentFieldValue(normalizedSelectedFitmentValues, field);
                const options = includeSelectedOption(
                    fieldOptions[key] ?? [{ label: getFitmentFieldEmptyOptionLabel(field, 'all'), value: '' }],
                    selectedValue,
                );
                const fieldIndex = productListSelectorFields.findIndex((candidate) => candidate === field);
                const yearFieldIndex = standardYearField
                    ? productListSelectorFields.findIndex((candidate) => candidate === standardYearField)
                    : -1;

                return (
                    <React.Fragment key={key}>
                        <div className="pf-fitment-selector__field">
                            <FitmentValueAutocomplete
                                label={field.label}
                                options={options}
                                value={selectedValue}
                                disabled={isFitmentFieldDisabled(productListSelectorFields, normalizedSelectedFitmentValues, field, false)}
                                loading={loadingFieldOptions}
                                placeholder={getFitmentFieldEmptyOptionLabel(field, 'select')}
                                onChange={(value) => {
                                    // Both mapping views share the configured
                                    // selector fields and normalized URL state.
                                    setSelectedFitmentValues((current) => updateFitmentFieldValue(productListSelectorFields, current, field, value));
                                    if (standardYearField && (field === standardYearField ? !value : fieldIndex < yearFieldIndex)) {
                                        setSelectedFitmentYearTo('');
                                    }
                                    setMappedOnly(false);
                                }}
                            />
                        </div>

                        {standardYearField && field === standardYearField && (
                            <div className="pf-fitment-selector__field">
                                <FitmentValueAutocomplete
                                    label="Year To"
                                    options={includeSelectedOption(fieldOptions[key] ?? [], selectedFitmentYearTo)}
                                    value={selectedFitmentYearTo}
                                    disabled={!selectedValue}
                                    loading={loadingFieldOptions}
                                    error={selectedYearRange.error}
                                    placeholder="Select ending year"
                                    onChange={(value) => {
                                        setSelectedFitmentYearTo(value);
                                        setMappedOnly(false);
                                    }}
                                />
                            </div>
                        )}
                    </React.Fragment>
                );
            })}

            {productListSelectorFields.length > 0 && (
                <div className="pf-fitment-selector__action">
                    <Button
                        onClick={() => {
                            setSelectedFitmentValues({});
                            setSelectedFitmentYearTo('');
                            setMappedOnly(false);
                        }}
                        disabled={!hasAnyFitmentFieldValue(productListSelectorFields, normalizedSelectedFitmentValues) && !selectedFitmentYearTo}
                    >
                        Clear
                    </Button>
                </div>
            )}
        </div>
    );

    return (
        <>
            <SaveBar id="products-mapping-save-bar" open={!isCollectionsView && !isAtdSourceTab && hasMappingDraftChanges}>
                <button onClick={discardMappingChanges} disabled={loading || mappingSubmitting}>
                    Discard
                </button>
                <button variant="primary" onClick={saveMappingChanges} disabled={loading || mappingSubmitting} loading={mappingSubmitting}>
                    Save
                </button>
            </SaveBar>
            <Page fullWidth>
                <div className="pf-products-page-header">
                    <Text as="h1" variant="headingLg">
                        {isCollectionsView ? 'Collections' : 'Products'}
                    </Text>
                    <div className="pf-products-page-header__actions">
                        {!isCollectionsView && sourceSwitch}
                        {mappingViewSwitch}
                    </div>
                </div>
                {isCollectionsView ? (
                    <Card>
                        {fitmentSelector}
                        <CollectionMappingsPanel
                            hasVehicleContext={hasVehicleContext}
                            selectedVehicleLabel={selectedVehicleLabel}
                            year={selectedYear}
                            yearTo={selectedYearToForRequest}
                            make={selectedMake}
                            model={selectedModel}
                            submodel={selectedLegacyValues.submodel}
                            fieldValues={normalizedSelectedFitmentValues}
                            onDirtyChange={setCollectionMappingDirty}
                        />
                    </Card>
                ) : isAtdSourceTab ? (
                    <Card>
                        <div style={{ display: 'grid', gap: 16 }}>
                            <div style={{
                                display: 'grid',
                                gridTemplateColumns: 'repeat(auto-fit, minmax(180px, 1fr))',
                                gap: 12,
                                alignItems: 'end',
                            }}>
                                <TextField
                                    label="Search ATD products"
                                    value={atdQuery}
                                    onChange={setAtdQuery}
                                    onClearButtonClick={() => setAtdQuery('')}
                                    clearButton
                                    autoComplete="off"
                                    placeholder="Part #, brand, product, UPC"
                                />
                                <Select label="Status" options={atdStatusOptions} value={atdStatus} onChange={setAtdStatus} />
                                <Select label="Link" options={atdLinkOptions} value={atdLinkStatus} onChange={setAtdLinkStatus} />
                                <Select label="Sort" options={atdSortOptions} value={atdSort} onChange={setAtdSort} />
                                <Button
                                    onClick={() => loadAtdProducts(atdPage, atdQuery, atdStatus, atdLinkStatus, atdSort)}
                                    loading={atdLoading}
                                >
                                    Refresh
                                </Button>
                            </div>

                            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 12, flexWrap: 'wrap' }}>
                                <Text as="p" variant="bodySm" tone="subdued">
                                    {atdTotalCount.toLocaleString()} ATD products
                                </Text>
                            </div>

                            <IndexTable
                                selectable
                                promotedBulkActions={atdPromotedBulkActions}
                                resourceName={{ singular: 'ATD product', plural: 'ATD products' }}
                                itemCount={atdProducts.length}
                                hasMoreItems={atdHasNext}
                                selectedItemsCount={allAtdResourcesSelected ? 'All' : selectedAtdResources.length}
                                onSelectionChange={handleAtdSelectionChange}
                                headings={atdProductTableHeadings}
                                loading={atdLoading}
                                pagination={{
                                    hasNext: atdHasNext,
                                    hasPrevious: atdPage > 1,
                                    onNext: () => loadAtdProducts(atdPage + 1, atdQuery, atdStatus, atdLinkStatus, atdSort),
                                    onPrevious: () => loadAtdProducts(atdPage - 1, atdQuery, atdStatus, atdLinkStatus, atdSort),
                                }}
                            >
                                {atdProducts.map((product, index) => {
                                    const status = getAtdStatus(product);
                                    const title = getAtdProductTitle(product);
                                    const displayTitle = truncateText(title, PRODUCT_TITLE_DISPLAY_LIMIT);
                                    const showTitleTooltip = title.length > PRODUCT_TITLE_DISPLAY_LIMIT;
                                    const linkedProductTitle = String(product.shopifyProductTitle || product.shopifyProductId);
                                    const linkedProductDisplayTitle = truncateText(linkedProductTitle, PRODUCT_TITLE_DISPLAY_LIMIT);
                                    const showLinkedProductTooltip = linkedProductTitle.length > PRODUCT_TITLE_DISPLAY_LIMIT;
                                    const titleLink = (
                                        <a
                                            href={`/products/atd/${product.id}`}
                                            onClick={(event) => {
                                                event.preventDefault();
                                                navigate(`/products/atd/${product.id}`);
                                            }}
                                            style={{ color: 'inherit', textDecoration: 'none', fontWeight: 600 }}
                                            onMouseEnter={(event) => { event.currentTarget.style.textDecoration = 'underline'; }}
                                            onMouseLeave={(event) => { event.currentTarget.style.textDecoration = 'none'; }}
                                            aria-label={title}
                                        >
                                            {displayTitle}
                                        </a>
                                    );

                                    return (
                                        <IndexTable.Row
                                            id={String(product.id)}
                                            key={product.id}
                                            position={index}
                                            selected={allAtdResourcesSelected || selectedAtdResources.includes(String(product.id))}
                                        >
                                            <IndexTable.Cell>
                                                <div style={{ display: 'flex', alignItems: 'center', gap: 10, minWidth: 0 }}>
                                                    {product.primaryImageUrl ? (
                                                        <Thumbnail source={product.primaryImageUrl} alt={title} size="small" />
                                                    ) : (
                                                        <Thumbnail source={ImageIcon} alt={title} size="small" />
                                                    )}
                                                    <div style={{ minWidth: 0 }}>
                                                        {showTitleTooltip ? (
                                                            <Tooltip
                                                                content={title}
                                                                preferredPosition="below"
                                                                width="wide"
                                                                accessibilityLabel={title}
                                                            >
                                                                {titleLink}
                                                            </Tooltip>
                                                        ) : titleLink}
                                                        <Text as="p" variant="bodySm" tone="subdued">{product.categoryName || '-'}</Text>
                                                    </div>
                                                </div>
                                            </IndexTable.Cell>
                                            <IndexTable.Cell>{product.partNumber || product.manufacturerPartNumber || '-'}</IndexTable.Cell>
                                            <IndexTable.Cell>{product.brandName || '-'}</IndexTable.Cell>
                                            <IndexTable.Cell>{product.size || '-'}</IndexTable.Cell>
                                            <IndexTable.Cell><Badge tone={status.tone}>{status.label}</Badge></IndexTable.Cell>
                                            <IndexTable.Cell>
                                                {product.shopifyProductId ? (
                                                    <div style={{ display: 'grid', gap: 2 }}>
                                                        <Text as="p" variant="bodySm">
                                                            {showLinkedProductTooltip ? (
                                                                <Tooltip
                                                                    content={linkedProductTitle}
                                                                    preferredPosition="below"
                                                                    width="wide"
                                                                    accessibilityLabel={linkedProductTitle}
                                                                >
                                                                    <span>{linkedProductDisplayTitle}</span>
                                                                </Tooltip>
                                                            ) : linkedProductDisplayTitle}
                                                        </Text>
                                                        <Text as="p" variant="bodySm" tone="subdued">{product.shopifyProductStatus || 'Linked'}</Text>
                                                    </div>
                                                ) : isAtdProductManuallyIncluded(product) ? (
                                                    <Badge tone="info">Manual link pending</Badge>
                                                ) : product.manualSyncAction === 'Unlink' ? (
                                                    <Badge tone="attention">Manually unlinked</Badge>
                                                ) : (
                                                    <Badge tone="attention">Unlinked</Badge>
                                                )}
                                            </IndexTable.Cell>
                                            <IndexTable.Cell>{product.availableQuantity ?? 0}</IndexTable.Cell>
                                        </IndexTable.Row>
                                    );
                                })}
                            </IndexTable>
                        </div>
                    </Card>
                ) : (
            <Card>
                {fitmentSelector}

                {hasVehicleContext && (
                    <div className="pf-products-vehicle-context">
                        <div className="pf-products-vehicle-context__summary">
                            <div className="pf-products-vehicle-context__details">
                                <Text as="p" variant="bodySm" tone="subdued">
                                    Selected vehicle
                                </Text>
                                <div className="pf-products-vehicle-context__vehicle">
                                    <Text as="p" variant="bodyMd" fontWeight="semibold">
                                        {selectedVehicleLabel}
                                    </Text>
                                    <Badge tone={displayedMappedCount === 0 ? 'critical' : undefined}>
                                        {displayedMappedCount == null
                                            ? 'Checking mappings'
                                            : `${displayedMappedCount.toLocaleString()} mapped product${displayedMappedCount === 1 ? '' : 's'}`}
                                    </Badge>
                                </div>
                            </div>
                            <div className="pf-products-vehicle-context__filter">
                                <Checkbox
                                    label="Show mapped only"
                                    checked={mappedOnly}
                                    onChange={setMappedOnly}
                                />
                            </div>
                        </div>
                        {displayedCollectionMapping && displayedCollectionMapping.state !== 'unmapped' && (
                            <div className={`pf-products-vehicle-context__mapping${displayedCollectionMapping.state === 'mixed'
                                ? ' pf-products-vehicle-context__mapping--mixed'
                                : ''}`}>
                                <div className="pf-products-vehicle-context__mapping-icon" aria-hidden="true">
                                    <Icon source={CollectionIcon} tone={displayedCollectionMapping.state === 'mixed' ? 'caution' : 'info'} />
                                </div>
                                <div className="pf-products-vehicle-context__mapping-copy">
                                    <Text as="p" variant="bodySm" fontWeight="semibold">
                                        {displayedCollectionMapping.state === 'mapped'
                                            ? 'This vehicle is mapped to a collection'
                                            : 'This year range has mixed collection mappings'}
                                    </Text>
                                    <Text as="p" variant="bodySm" tone="subdued">
                                        {displayedCollectionMapping.state === 'mapped' ? (
                                            <>
                                                Mapped collection: <strong>“{displayedCollectionMapping.collectionTitle}”</strong>.{' '}
                                                Product mappings for this vehicle remain saved. How this mapping is used on the storefront is controlled in Settings.
                                            </>
                                        ) : (
                                            `${displayedCollectionMapping.mappedVehicleCount} of ${displayedCollectionMapping.selectedVehicleCount} selected vehicles are mapped to collections.`
                                        )}
                                    </Text>
                                </div>
                                <Button size="slim" onClick={() => handleMappingViewSelect('collections')}>
                                    Manage mapping
                                </Button>
                            </div>
                        )}
                    </div>
                )}
                {!hasVehicleContext && (
                    <div style={{ margin: '0 10px 16px' }}>
                        <label style={{ display: 'flex', alignItems: 'center', gap: 6, fontSize: 13, color: '#8a8a8a' }}>
                            <input type="checkbox" checked={false} disabled />
                            Show mapped only
                        </label>
                    </div>
                )}

                <IndexFilters
                    sortOptions={sortOptions}
                    sortSelected={sortSelected}
                    onSort={setSortSelected}
                    loading={loading}
                    cancelAction={{ onAction: onHandleCancel, disabled: false, loading: false }}
                    tabs={tabs}
                    selected={selectedTab}
                    onSelect={setSelectedTab}
                    queryValue={queryValue}
                    queryPlaceholder="Search products"
                    onQueryChange={setQueryValue}
                    onQueryClear={handleQueryClear}
                    onClearAll={() => {
                        setHasFitment([]);
                        setVendor('');
                        setTaggedWith('');
                        setQueryValue('');
                    }}
                    filters={filters}
                    appliedFilters={appliedFilters}
                    mode={mode}
                    setMode={setMode}
                />

                <div onClickCapture={handleTableClickCapture}>
                    <IndexTable
                    selectable={!hasVehicleContext}
                    bulkActions={hasVehicleContext ? [] : bulkActions}
                    promotedBulkActions={hasVehicleContext ? [] : promotedBulkActions}
                    resourceName={{ singular: 'product', plural: 'products' }}
                    itemCount={sortedResourceProducts.length}
                    hasMoreItems={hasNext}
                    selectedItemsCount={hasVehicleContext ? 0 : (allResourcesSelected ? 'All' : selectedResources.length)}
                    onSelectionChange={hasVehicleContext ? undefined : handleSelectionChange}
                    headings={productTableHeadings}
                    pagination={{
                        hasNext,
                        hasPrevious: page > 1,
                        onNext: async () => {
                            if (useLocalProductList) {
                                await loadProducts(page + 1, queryValue, '', sortSelected[0] || 'created desc');
                                return;
                            }

                            if (!nextCursor) return;
                            setCursorByPage((prev) => ({ ...prev, [page + 1]: nextCursor }));
                            await loadProducts(page + 1, queryValue, nextCursor, sortSelected[0] || 'created desc');
                        },
                        onPrevious: async () => {
                            if (useLocalProductList) {
                                await loadProducts(page - 1, queryValue, '', sortSelected[0] || 'created desc');
                                return;
                            }

                            await loadProducts(page - 1, queryValue, cursorByPage[page - 1] ?? '', sortSelected[0] || 'created desc');
                        },
                    }}
                    loading={loading}
                >
                    {sortedResourceProducts.map((product, index) => {
                        const mappingRowState = hasVehicleContext ? getMappingRowState(product.id) : 'unmapped';
                        const rowId = hasVehicleContext ? `pf-map-${mappingRowState}-${product.id}` : product.id;
                        const displayTitle = truncateText(product.title, PRODUCT_TITLE_DISPLAY_LIMIT);
                        const showTitleTooltip = product.title.length > PRODUCT_TITLE_DISPLAY_LIMIT;
                        const titleLink = (
                            <a
                                href={buildProductAdminUrl(product.id)}
                                onClick={(event) => handleTitleLinkClick(event, product.id)}
                                style={{ color: 'inherit', textDecoration: 'none' }}
                                onMouseEnter={(event) => { event.currentTarget.style.textDecoration = 'underline'; }}
                                onMouseLeave={(event) => { event.currentTarget.style.textDecoration = 'none'; }}
                                aria-label={product.title}
                            >
                                {displayTitle}
                            </a>
                        );
                        const railColor = hasVehicleContext
                            ? getMappingRailColor(mappingRowState)
                            : product.fitmentCount > 0
                                ? '#2f855a'
                                : 'transparent';

                        return (
                            <IndexTable.Row
                                id={rowId}
                                key={product.id}
                                position={index}
                                selected={hasVehicleContext ? false : (allResourcesSelected ? true : selectedResources.includes(product.id))}
                                onClick={() => navigate(`/products/${product.id}`)}
                            >
                                {hasVehicleContext && (
                                    <IndexTable.Cell>
                                        <div onClick={(event) => event.stopPropagation()}>
                                            <Checkbox
                                                label={`Map ${product.title} to ${selectedVehicleLabel}`}
                                                labelHidden
                                                checked={draftMappedProductIdSet.has(product.id)}
                                                onChange={(checked) => toggleProductMapping(product.id, checked)}
                                                disabled={loading || mappingSubmitting}
                                            />
                                        </div>
                                    </IndexTable.Cell>
                                )}
                                <IndexTable.Cell>
                                    <div style={{ display: 'flex', alignItems: 'center', gap: 8, borderLeft: `3px solid ${railColor}`, paddingLeft: 8 }}>
                                        {product.featuredImage ? (
                                            <Thumbnail source={product.featuredImage} alt={product.title} size="small" />
                                        ) : (
                                            <Thumbnail source={ImageIcon} alt={product.title} size="small" />
                                        )}
                                        <span style={{ display: 'inline-flex' }}>
                                            {showTitleTooltip ? (
                                                <Tooltip
                                                    content={product.title}
                                                    preferredPosition="below"
                                                    width="wide"
                                                    accessibilityLabel={product.title}
                                                >
                                                    {titleLink}
                                                </Tooltip>
                                            ) : titleLink}
                                        </span>
                                    </div>
                                </IndexTable.Cell>
                                <IndexTable.Cell>{product.isAtdProduct ? 'ATD' : 'Shopify'}</IndexTable.Cell>
                                <IndexTable.Cell>{product.sku || '-'}</IndexTable.Cell>
                                <IndexTable.Cell>{product.fitmentCount}</IndexTable.Cell>
                                <IndexTable.Cell />
                            </IndexTable.Row>
                        );
                    })}
                    </IndexTable>
                </div>
            </Card>
                )}
        </Page>
        </>
    );
};

function isEmpty(value: string | string[]): boolean {
    if (Array.isArray(value)) return value.length === 0;
    return value === '' || value == null;
}

export default observer(ProductsRealtimePage);
